/**
 * Générateur — Attendu 970 — Résoudre des problèmes faisant appel à des
 * conversions d'unités de longueur et d'unités d'aires.
 *
 * Niveau : 5ème | Thème #11 Parallélogrammes (O29) | Domaine : Espace et géométrie
 * Source prof : cluster 4, Créé n°43 + Créé n°44, extraction
 * exerciseur/parallelogrammes (+ conversions d'aire réinvesties d'Ex.4/Ex.7 O28).
 *
 * 4 formats / 4 cartes :
 *  1. conversion   — drill : convertir une aire (QCM) + piège d'unité km²/km³.
 *  2. equivalences — relier des aires équivalentes (association).
 *  3. carte        — Créé n°43 : carte au 1/100 000, aire réelle km² puis m² (saisie).
 *  4. champ        — Créé n°44 : aire du champ m² → km², masse d'engrais kg (saisie).
 *
 * INVARIANTS : conversions exactes par facteur ×100 par échelon ; valeurs
 * choisies pour des résultats entiers ou à décimales finies.
 */

import {
    randInt, randomChoice, shuffle,
    makeQcm, makeRelier, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 970;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CONV = 'Convertir une aire';
const L_EQUIV = 'Aires équivalentes';
const L_CARTE = 'La carte au 1/100 000';
const L_CHAMP = 'Le champ';

const fr = (x) => String(x).replace('.', ',');

// ═══════════════════════════════════════════════════════════
// Carte 1 — Convertir une aire (QCM) + piège d'unité
// ═══════════════════════════════════════════════════════════

const UNITES = ['mm²', 'cm²', 'dm²', 'm²', 'dam²', 'hm²', 'km²'];
const PAIRES_CONV = [
    ['m²', 'cm²'], ['km²', 'm²'], ['cm²', 'mm²'],
    ['dm²', 'cm²'], ['m²', 'dm²'], ['hm²', 'dam²'],
];

const genConversionNumerique = () => {
    let from, to, steps, V, correct, choix;
    for (let essai = 0; essai < 200; essai++) {
        [from, to] = randomChoice(PAIRES_CONV);
        steps = UNITES.indexOf(from) - UNITES.indexOf(to);   // > 0 (vers plus petit → ×100^steps)
        V = randInt(2, 9);
        const bon = V * 100 ** steps;
        const set = [
            bon,
            V * 10 ** steps,          // confond ×10 et ×100
            V * 100 ** (steps + 1),   // un échelon de trop
            V,                        // oubli de convertir
        ];
        if (new Set(set).size === 4) {
            correct = `${bon} ${to}`;
            choix = set.map((n) => `${n} ${to}`);
            break;
        }
    }
    const texte = `<p>Convertis ${V} ${from} en ${to}.</p>`;
    const payload = makeQcm(choix, correct, texte, { ...opts(L_CONV) });
    payload._meta.generatorKey = 'conversion_qcm_unique';
    return payload;
};

const genConversionUnite = () => {
    const A = randomChoice([20000, 30000, 60000, 90000, 120000]);
    const correct = 'km²';
    const distract = ['km³', 'm³', 'km'];
    const texte = `<p>L'aire d'un champ vaut ${A.toLocaleString('fr-FR')} m². `
        + `C'est une aire : dans quelle grande unité peut-on aussi l'exprimer ?</p>`;
    const payload = makeQcm([correct, ...distract], correct, texte, { ...opts(L_CONV) });
    payload._meta.generatorKey = 'conversion_qcm_unique';
    return payload;
};

const genConversion = () =>
    (Math.random() < 0.75 ? genConversionNumerique() : genConversionUnite());

// ═══════════════════════════════════════════════════════════
// Carte 2 — Aires équivalentes (relier), association
// ═══════════════════════════════════════════════════════════

const genEquivalences = () => {
    const k = () => randInt(2, 9);
    const [k1, k2, k3, k4] = [k(), k(), k(), k()];
    const pairs = shuffle([
        { source: `${k1} m²`, target: `${k1 * 10000} cm²` },
        { source: `${k2} cm²`, target: `${k2 * 100} mm²` },
        { source: `${k3} km²`, target: `${k3 * 1000000} m²` },
        { source: `${k4} dm²`, target: `${k4 * 100} cm²` },
    ]);
    const texte = '<p>Relie chaque aire à l\'aire qui lui est égale.</p>';
    const payload = makeRelier(pairs, texte, { ...opts(L_EQUIV) });
    payload._meta.generatorKey = 'equivalences_relier';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — La carte au 1/100 000 (Créé n°43), saisie
// ═══════════════════════════════════════════════════════════

const genCarte = () => {
    const bc = randInt(2, 4), hc = randInt(2, 3), n = randInt(10, 20);
    const aireUn = bc * hc;             // cm² sur la carte
    const km2 = n * aireUn;             // 1 cm² ↔ 1 km²
    const m2 = km2 * 1000000;
    const phrase =
        `a. L'aire d'un parallélogramme sur la carte vaut {{${aireUn}}} cm². `
        + `b. Comme 1 cm représente 1 km, 1 cm² de carte représente {{1}} km² dans la réalité. `
        + `c. L'aire réelle de la réserve vaut donc environ {{${km2}}} km², soit {{${m2}}} m².`;
    const texte = '<p>Sur une carte à l\'échelle $\\dfrac{1}{100000}$, 1 cm représente 1 km dans la réalité. '
        + `On recouvre entièrement une réserve naturelle avec ${n} parallélogrammes identiques `
        + `de base ${bc} cm et de hauteur ${hc} cm (mesures sur la carte). Complète :</p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_CARTE), mode: 'saisie', strict: false,
    });
    payload._meta.generatorKey = 'carte_texte_trou_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Le champ (Créé n°44), saisie
// ═══════════════════════════════════════════════════════════

const genChamp = () => {
    const bm = randomChoice([200, 300, 400]);         // base en m (multiple de 100)
    const hm = randomChoice([80, 100, 120, 150, 200]); // hauteur en m (multiple de 10)
    const g = randomChoice([2, 3, 5]);
    const aire = bm * hm;                 // m² (multiple de 1000)
    const km2 = aire / 1000000;           // décimales finies
    const kg = (aire * g) / 1000;         // entier
    const hkm = fr(hm / 1000);            // hauteur exprimée en km (ex : 0,12)
    const phrase =
        `a. L'aire du champ vaut {{${aire}}} m². `
        + `b. Convertie en kilomètres carrés, cela fait {{${fr(km2)}}} km². `
        + `c. Il faut {{${kg}}} kg d'engrais.`;
    const texte = `<p>Un champ a la forme d'un parallélogramme de base ${bm} m et de hauteur `
        + `relative ${hkm} km. On épand un engrais à raison de ${g} g par m². `
        + 'a. Calcule son aire en m². b. Convertis cette aire en km². '
        + 'c. Quelle masse d\'engrais, en kg, faut-il ?</p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_CHAMP), mode: 'saisie', strict: false,
    });
    payload._meta.generatorKey = 'champ_texte_trou_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    conversion_qcm_unique: genConversion,
    equivalences_relier: genEquivalences,
    carte_texte_trou_saisie: genCarte,
    champ_texte_trou_saisie: genChamp,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', relier: 'Relier', texte_trou_saisie: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'conversion', label: L_CONV, formatsList: buildFormats('conversion') },
    { id: 'equivalences', label: L_EQUIV, formatsList: buildFormats('equivalences') },
    { id: 'carte', label: L_CARTE, formatsList: buildFormats('carte') },
    { id: 'champ', label: L_CHAMP, formatsList: buildFormats('champ') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { genConversion, genEquivalences, genCarte, genChamp };
